import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/app/DashboardLayout';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/stores/auth';
import { Card, EmptyState } from '@/features/common/ui';
import { AskTab } from './AskTab';
import { ChaseTab } from './ChaseTab';
import { DuesTab } from './DuesTab';
import { SaveTab } from './SaveTab';
import { DebtTab } from './DebtTab';

const TABS = [
  { key: 'ask', label: 'Ask' },
  { key: 'chase', label: 'Chase' },
  { key: 'dues', label: 'Dues' },
  { key: 'save', label: 'Save' },
  { key: 'debt', label: 'Debt' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export const CFO_DISCLAIMER =
  'Information and calculations from your books, not investment, tax or legal advice. Fint is not a SEBI-registered investment adviser and recommends no financial product. Rates and due dates as researched; confirm with your CA.';

/**
 * The CFO tab (docs/design/ai-cfo.md §4): talk to the books, chase what is
 * owed, see what is due, find what leaks, and plan what is owed.
 *
 * One page with five parts, chosen by `?tab=` so a link from an advice card
 * or an answer lands on the right one. Nothing here changes the screens it
 * links to; every action that reaches a customer or a bank still needs a
 * person's tap.
 */
export function CfoPage() {
  const perms = usePermissions();
  // Platform staff only until the CFO's release (docs/roadmap.md); the API refuses anyone else too.
  const staff = useAuthStore((s) => !!s.user?.cfo);
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: TabKey = TABS.some((t) => t.key === raw) ? (raw as TabKey) : 'ask';

  const select = (next: TabKey) => {
    const copy = new URLSearchParams(params);
    copy.set('tab', next);
    copy.delete('thread');
    setParams(copy, { replace: true });
  };

  if (!perms.seesAll('report') || !staff) {
    return (
      <>
        <PageHeader title="CFO" />
        <div className="px-4 sm:px-5">
          <Card>
            <EmptyState title="Not available" description="The CFO tab adds up the whole book, so it needs access to all of its reports." />
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="CFO" subtitle="Ask about your money, chase what you are owed, and see what is due, what leaks and what you owe." />
      <div className="space-y-4 px-4 pb-10 sm:px-5" data-cfo-page={tab}>
        <div role="tablist" aria-label="CFO" className="flex flex-wrap gap-1 rounded-full border border-border p-0.5 sm:w-fit">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => select(t.key)}
              className={
                tab === t.key
                  ? 'inline-flex flex-1 items-center justify-center rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary touch:min-h-11 sm:flex-none'
                  : 'inline-flex flex-1 items-center justify-center rounded-full px-3 py-1 text-sm font-medium text-muted-foreground hover:bg-muted active:bg-muted touch:min-h-11 sm:flex-none'
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        <div role="tabpanel">
          {tab === 'ask' ? <AskTab /> : null}
          {tab === 'chase' ? <ChaseTab /> : null}
          {tab === 'dues' ? <DuesTab /> : null}
          {tab === 'save' ? <SaveTab /> : null}
          {tab === 'debt' ? <DebtTab /> : null}
        </div>

        <p className="text-xs text-muted-foreground">{CFO_DISCLAIMER}</p>
      </div>
    </>
  );
}
